/**
 * SLIK Connect quick-templates — one-tap canned messages for the staff composer. DERIVED from Jose's real
 * Fresha Connect threads (2026-10-20 snapshot; see SLIK-CONNECT-INBOUND-NOTIFY-SPEC.md §3b). This is the
 * PRE-AI quick win: no AI needed, just reusable chips so staff stop retyping the same few messages.
 *
 * Two kinds:
 *  - Short acks (`fillThenEdit` falsey) → SEND immediately on tap (no blanks to fill).
 *  - Outreach (`fillThenEdit: true`) → LOAD into the composer so staff can fill {barber}/{date}/{time}
 *    before sending (these have blanks on purpose).
 *
 * Merge fields filled automatically where known ({client}, {firstName}, {shop}); the rest ({barber},
 * {barber2}, {date}, {time}, {dateRange}, {holiday}, {service}) stay visible as `{…}` for staff to type.
 * Render with `renderTemplate` from messagingConfig (unknown placeholders are left visible on purpose).
 */

export interface ConnectTemplate {
  label: string;           // short chip text
  body: string;            // message with {placeholders}
  fillThenEdit?: boolean;  // true = load into composer to edit; otherwise send on tap
}

export const CONNECT_TEMPLATES: ConnectTemplate[] = [
  // ── Short acks — send on tap (from the "on my way" / "thanks" threads) ───────────────────────────
  { label: '👍 See you soon', body: 'Thanks for the heads up — see you soon! 💈' },
  { label: '🙏 Anytime', body: 'Anytime — see you then! 🙏' },
  { label: 'Running behind', body: 'Running a few minutes behind — thanks for your patience! We\'ll get you in shortly.' },
  { label: 'Ready for you', body: 'Ready when you are — come on in. ✂️' },

  // ── Outreach — load into composer, fill the blanks, then send (from Jose/Diana's real outreach) ──
  {
    label: 'Barber out',
    fillThenEdit: true,
    body: 'Hello {client}, this is {barber} from SLIK Barber Lounge. I\'m reaching out because {barber2} ' +
      'won\'t be in {date} — would you like to reschedule, or should we reach back out when they\'re ' +
      'available? Sorry for the inconvenience.',
  },
  {
    label: 'Move appt',
    fillThenEdit: true,
    body: 'Hello {client}, this is {barber} from SLIK Barber Lounge. I\'m reaching out because I need to ' +
      'move your appointment on {date} — what time works for you?',
  },
  {
    label: 'Closure notice',
    fillThenEdit: true,
    body: 'Hi {client}, this is {barber} from SLIK. We\'ll be closed {dateRange} for {holiday}. Give us a ' +
      'call or reply here if you\'d like to come in before, or anytime after. Thank you!',
  },
  {
    label: 'Confirm change',
    fillThenEdit: true,
    body: 'Got it, {client} — I\'ll update your appointment to {service}. See you {date} at {time}! 👍',
  },
];
